import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CaretDown, CaretUp, Check } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

// Branded form-select primitive — replaces every native <select> across
// the SPA so the OPEN dropdown menu looks on-brand instead of OS-default
// gray. Built on @radix-ui/react-select; styled to match the existing
// dropdown-menu.tsx language so an action menu and a form select feel
// like cousins, not strangers.
//
// Usage mirrors shadcn/ui:
//
//   <Select value={x} onValueChange={setX}>
//     <SelectTrigger><SelectValue placeholder="Pick one" /></SelectTrigger>
//     <SelectContent>
//       <SelectItem value="a">Option A</SelectItem>
//       <SelectItem value="b">Option B</SelectItem>
//     </SelectContent>
//   </Select>
//
// Keyboard: arrow keys navigate, Enter selects, Esc closes, type-ahead
// finds matching options. Focus trap + outside-click dismissal are
// Radix-handled. Works inside a Sheet / Dialog without the DismissableLayer
// race that bit us on LocationFormModal — Radix Select is registered as
// a child layer.

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      // Pill shape + design-system token colors so the trigger reads
      // as a sibling of <Input> and <Button variant="outline">. The
      // [&>span]:line-clamp-1 keeps long values truncating cleanly
      // without bloating the height.
      'flex min-h-9 w-full items-center justify-between gap-2 rounded-pill border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground transition-colors',
      'placeholder:text-muted-foreground hover:bg-muted',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'data-[placeholder]:text-muted-foreground',
      '[&>span]:line-clamp-1',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <CaretDown className="h-4 w-4 shrink-0 opacity-60" weight="bold" aria-hidden />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <CaretUp className="h-4 w-4" weight="bold" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <CaretDown className="h-4 w-4" weight="bold" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

type SelectContentProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
  // container forwards to Radix Portal. Set this to a native <dialog>
  // element when the Select is rendered inside one — `dialog.showModal()`
  // puts the dialog in the browser's top-layer, and anything portaled
  // to document.body (Radix's default) sits BELOW the top-layer, which
  // is why dropdowns inside a native dialog appear hidden behind it.
  // Portaling into the dialog itself makes the dropdown share the same
  // top-layer stacking context.
  container?: HTMLElement | null;
};

export const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>(({ className, children, position = 'popper', container, ...props }, ref) => (
  <SelectPrimitive.Portal container={container ?? undefined}>
    <SelectPrimitive.Content
      ref={ref}
      // popper positioning anchors to the trigger; matches the dropdown
      // menu's behavior. min-w-[var(--radix-select-trigger-width)] makes
      // the menu match the trigger's width by default — long option
      // labels can still grow the menu via the override below.
      position={position}
      className={cn(
        // z-[200] outranks every overlay in the app (modals sit at z-[60]–z-[100]).
        // A dropdown is always the topmost thing on screen while it's open, and
        // when `container` is absent Radix portals to document.body — at z-50
        // that landed BEHIND the modal that opened it, so the menu mounted
        // invisibly and the only visible effect was the scroll-lock nudging the
        // page a couple of pixels. Never lower this below the overlay scale.
        'relative z-[200] max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
        className,
      )}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

export const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'px-2 py-1.5 font-heading text-[11px] font-semibold uppercase tracking-wider text-muted-foreground',
      className,
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-md py-1.5 pl-8 pr-2 text-sm outline-none transition-colors',
      // Highlight (focus / hover via Radix's data-highlighted) uses the
      // soft accent — the lime fill is reserved for "selected" so the
      // two states are distinguishable when the user is hovering past
      // the current selection.
      'data-[highlighted]:bg-muted data-[highlighted]:text-foreground',
      'data-[state=checked]:font-semibold',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4 text-[var(--lime,#BEE034)]" weight="bold" aria-hidden />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

export const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-border', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
